import { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { apiHandler } from "../../api";

export const useRsfAnalyticsDashboard = () => {
  const dispatch = useDispatch();
  const { authData, storeId } = useSelector((state) => state.loginSlice);

  const [loading, setLoading] = useState(false);
  const [rawData, setRawData] = useState(null);
  const [startMonth, setStartMonth] = useState("");
  const [endMonth, setEndMonth] = useState("");
  const [selectedBuyer, setSelectedBuyer] = useState(
    "pramaan.ondc.org/beta/preprod/mock/buyer",
  );

  const API_BASE_URL =
    "https://preprod.appwrapper.clouddeploy.in/route/rsf/futuresoft/ondc/rsf/api/v2/analytics/dashboard";

  const defaultBuyers = useMemo(
    () => [
      "buyer-app-preprod-v2.ondc.org",
      "pramaan.ondc.org/beta/preprod/mock/buyer",
    ],
    [],
  );

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        buyers_order_analysis: JSON.stringify(defaultBuyers),
        buyer_analysis: selectedBuyer,
        is_buyer_analysis: "true",
      });

      // Only add date parameters if they are provided
      if (startMonth) {
        params.append("from", startMonth);
      }
      if (endMonth) {
        params.append("to", endMonth);
      }

      const res = await apiHandler({
        url: `${API_BASE_URL}?${params.toString()}`,
        method: "GET",
        authToken: authData,
        dispatch,
        storeId,
      });

      if (res?.data?.code === 200) {
        setRawData(res.data.data);
      }
    } catch (err) {
      //console.error("Analytics Error:", err);
    } finally {
      setLoading(false);
    }
  }, [
    startMonth,
    endMonth,
    selectedBuyer,
    authData,
    dispatch,
    storeId,
    defaultBuyers,
  ]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const comparisonChartData = useMemo(() => {
    const data = rawData?.buyers_order_analysis?.Orderdata || [];
    return {
      categories: data.map((d) => d.displayLabel || ""),
      series: defaultBuyers.map((buyerName) => ({
        name: buyerName.split(/[./]/)[0],
        data: data.map(
          (d) => d.buyers?.find((b) => b.buyer === buyerName)?.count || 0,
        ),
      })),
    };
  }, [rawData, defaultBuyers]);
  const settlementChartData = useMemo(() => {
    const data = rawData?.buyer_analysis?.Orderdata || [];

    return {
      categories: data.map((d) => d.displayLabel || ""),
      series: [
        {
          name: "Order Count",
          type: "line",
          data: data.map((d) => d.buyers?.[0]?.count || 0),
          yaxis: 0,
        },
        {
          name: "Requested Amount",
          type: "column",
          data: data.map((d) => d.buyers?.[0]?.requested_amount || 0),
          yaxis: 1,
        },
        {
          name: "Settled Amount",
          type: "column",
          data: data.map((d) => d.buyers?.[0]?.settled_amount || 0),
          yaxis: 1,
        },
      ],
    };
  }, [rawData]);

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        buyers_order_analysis: JSON.stringify(defaultBuyers),
        buyer_analysis: selectedBuyer,
        is_buyer_analysis: "true",
      });

      if (startMonth) {
        params.append("from", startMonth);
      }
      if (endMonth) {
        params.append("to", endMonth);
      }

      const res = await apiHandler({
        url: `${API_BASE_URL}?${params.toString()}`,
        method: "GET",
        authToken: authData,
        dispatch,
        storeId,
      });

      if (res?.data?.code === 200) {
        setRawData(res.data.data);
      }
    } catch (err) {
      //console.error("Analytics Error:", err);
    } finally {
      setLoading(false);
    }
  }, [startMonth, endMonth, selectedBuyer, authData, dispatch, storeId, defaultBuyers]);

  return {
    loading,
    comparisonChartData,
    settlementChartData,
    selectedBuyer,
    setSelectedBuyer,
    startMonth,
    setStartMonth,
    endMonth,
    setEndMonth,
    availableBuyers: defaultBuyers,
    handleRefresh,
  };
};
